import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff, FileText, Briefcase, Users, TrendingUp } from 'lucide-react';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login();
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const features = [
    {
      icon: <FileText className="feature-icon" />,
      title: "68+ Types de Documents",
      description: "Générez tous vos documents administratifs, commerciaux et RH"
    },
    {
      icon: <Briefcase className="feature-icon" />,
      title: "Gestion Complète",
      description: "Factures, devis, fiches de paie et bien plus encore"
    },
    {
      icon: <Users className="feature-icon" />,
      title: "Multi-Utilisateurs",
      description: "Gestion d'équipe avec permissions personnalisées"
    },
    {
      icon: <TrendingUp className="feature-icon" />,
      title: "Rapports Avancés",
      description: "Analyses et statistiques détaillées de votre activité"
    }
  ];

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="background-pattern"></div>
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      <div className="login-content">
        {/* Left Panel - Features */}
        <div className="login-left">
          <div className="brand-section">
            <div className="brand-logo">
              <img src="/invoit-logo-new.png" alt="INVOIT" className="brand-logo-image" />
            </div>
            <h1 className="brand-title">INVOIT</h1>
            <p className="brand-subtitle">
              Plateforme d'Automatisation Documentaire
            </p>
          </div>

          <div className="features-section">
            <h2 className="features-title">
              Automatisez vos documents professionnels
            </h2>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  {feature.icon}
                  <div className="feature-content">
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="stats-section">
            <div className="stat-item">
              <span className="stat-number">68+</span>
              <span className="stat-label">{t('login.stats.documents')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">PDF</span>
              <span className="stat-label">{t('login.stats.export')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">100%</span>
              <span className="stat-label">{t('login.stats.language')}</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="login-right">
          <div className="login-form-container">
            <div className="login-header">
              <h2 className="login-title">{t('login.title')}</h2>
              <p className="login-subtitle">
                {t('login.subtitle')}
              </p>
            </div>

            <div className="simple-login">
              <div className="welcome-message">
                <p>{t('login.welcome')}</p>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                className={`login-button ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  t('login.login_button')
                )}
              </button>
            </div>

            <div className="login-footer">
              <div className="access-info">
                <h4>Accès direct</h4>
                <p>Un simple clic pour accéder à tous vos outils de gestion documentaire</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 